import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Timer, ScanSearch, HeartHandshake, Building2, HandHeart } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Stagger, StaggerItem, LineReveal, Chapter } from "@/components/Reveal";
import { BUSINESS } from "@/data/site";

const VALUES = [
  { icon: ShieldCheck, title: "Responsibility", text: "We take ownership of every job, arriving prepared and treating your space with the care it deserves." },
  { icon: Timer, title: "Punctuality", text: "We respect your schedule. When we set a time, we keep it — and we work efficiently while we're with you." },
  { icon: ScanSearch, title: "Attention to Detail", text: "The difference between tidy and truly clean is in the details. That's where we focus our energy." },
  { icon: Building2, title: "Organized Spaces", text: "We leave every home and business not just clean, but organized — so it stays pleasant long after we leave." },
  { icon: HeartHandshake, title: "Pleasant Environments", text: "A clean space changes how it feels to live and work in it. That feeling is what we're really delivering." },
  { icon: HandHeart, title: "Professional Service", text: "Friendly, respectful, and easy to work with — professional customer service from the first call to the final walkthrough." },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Us | Julisa's House Cleaning — Kansas City"
        description="Julisa's House Cleaning is a local cleaning company dedicated to professional residential and commercial cleaning across the Kansas City metro — built on responsibility, punctuality, and attention to detail."
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-200/60 via-sky-100/50 to-white pt-14 pb-16 sm:pt-20 sm:pb-24">
        <div className="pointer-events-none absolute -top-24 -left-24 h-80 w-80 rounded-full bg-sky-100/70 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center [&>a]:mx-auto">
            <p className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
              About Us
            </p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              <LineReveal lines={["A local team that", <>cleans like it's <span className="text-sky-600 italic">our own home</span></>]} />
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              Julisa's House Cleaning is a cleaning company dedicated to providing professional
              services for homes and businesses throughout the Kansas City metropolitan area.
              We're not a big franchise — we're a local team that believes great cleaning comes
              down to showing up on time, caring about the details, and treating every space
              with respect.
            </p>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              From weekly house cleaning in Overland Park to office cleaning in Kansas City,
              we work with responsibility, punctuality, and attention to detail to ensure
              clean, organized, and pleasant spaces — every single visit.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/quote"
                data-testid="about-quote-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 active:scale-[0.98]"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="about-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-slate-800 transition-all duration-200 hover:border-sky-300 hover:bg-sky-50 active:scale-[0.98]"
              >
                <Phone className="h-4 w-4 text-sky-600" aria-hidden="true" /> {BUSINESS.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sky-100/60 border-y border-sky-200/60 py-20 sm:py-28" aria-labelledby="our-values">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter
            center
            index="01"
            label="What We Stand For"
            title="Six things you'll notice when you work with us."
          />
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v) => (
              <StaggerItem key={v.title}>
                <div className="group h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                    <v.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Reveal className="mx-auto max-w-4xl text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
            We'd love to clean for you.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
            Tell us about your home or business — we'll put together a free quote and a cleaning plan that fits your space.
          </p>
          <Link
            to="/quote"
            data-testid="about-cta-quote-button"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 active:scale-[0.98]"
          >
            Request a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
